import React from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CustomPage } from "@/types/custom-page";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";

interface CustomPagesTableProps {
  pages: CustomPage[];
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onToggleEnabled: (page: CustomPage, enabled: boolean) => void;
  onDelete: (page: CustomPage) => void;
  isUpdating: boolean;
}

export const CustomPagesTable: React.FC<CustomPagesTableProps> = ({
  pages,
  currentPage,
  totalPages,
  isLoading,
  onPageChange,
  onToggleEnabled,
  onDelete,
  isUpdating,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));

  const columns: Column<CustomPage>[] = [
    {
      key: "title",
      header: "Title",
      render: (_, row) => (
        <div className="min-w-[260px]">
          <div className="font-medium text-zinc-900">{row.title}</div>
          <div className="text-sm text-zinc-500">{row.buttonTitle}</div>
        </div>
      ),
    },
    {
      key: "enabled",
      header: "Enabled",
      className: "w-[140px]",
      render: (value, row) => (
        <div
          className="flex items-center gap-3"
          onClick={(event) => event.stopPropagation()}
        >
          <Switch
            checked={Boolean(value)}
            disabled={isUpdating}
            onCheckedChange={(checked) => onToggleEnabled(row, checked)}
          />
          <span className="text-sm text-zinc-600">
            {value ? "Enabled" : "Disabled"}
          </span>
        </div>
      ),
    },
    {
      key: "updatedAt",
      header: "Updated",
      className: "w-[180px]",
      render: (value) => (
        <span className="text-sm text-zinc-600">{formatDate(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-[120px]",
      render: (_, row) => (
        <div
          className="flex justify-end gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            variant="new_ghost"
            size="icon_sm"
            onClick={() => navigate(`/custom-pages/${row.id}`)}
            aria-label={`Edit ${row.title}`}
          >
            <Pencil />
          </Button>
          <Button
            variant="new_ghost"
            size="icon_sm"
            disabled={isUpdating}
            onClick={() => onDelete(row)}
            aria-label={`Delete ${row.title}`}
          >
            <Trash2 className="text-custom-destructive-dark" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      data={pages}
      columns={columns}
      loading={isLoading}
      emptyMessage="No custom pages found"
      withSorting={false}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      withPagination={totalPages > 1}
    />
  );
};
